import React, { useEffect, useMemo, useState } from "react";
import AutoNewsAdmin from "../components/AutoNewsAdmin";
import NewsAlertsAdmin from "../components/NewsAlertsAdmin";
import MaintenanceAdmin from "../components/MaintenanceAdmin";
import {
  Newspaper, RefreshCw, Loader2, X, TrendingUp, TrendingDown, Minus, Radio,
  CalendarClock, AlertTriangle, ArrowUpRight, ArrowDownRight, Sparkles, ExternalLink, Flame,
  Bitcoin, Coins, Landmark, BarChart2, Globe,
} from "lucide-react";
import EventCountdown from "../components/EventCountdown";
import { istTime, istDate, istDateTime, IST_LABEL } from "../utils/time";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_META = {
  Crypto: { c: "#fbbf24", bg: "rgba(251,191,36,0.14)", Icon: Bitcoin },
  Commodities: { c: "#f59e0b", bg: "rgba(245,158,11,0.14)", Icon: Coins },
  Forex: { c: "#38bdf8", bg: "rgba(56,189,248,0.14)", Icon: Globe },
  Macro: { c: "#c4b5fd", bg: "rgba(139,92,246,0.16)", Icon: Landmark },
  Stocks: { c: "#34d399", bg: "rgba(52,211,153,0.14)", Icon: BarChart2 },
  Markets: { c: "#7dd3fc", bg: "rgba(59,130,246,0.14)", Icon: Newspaper },
};
const catMeta = (c) => CAT_META[c] || CAT_META.Markets;

const biasTone = (b) =>
  b === "Bullish" ? { c: "ws-up", bg: "rgba(52,211,153,0.12)", bd: "rgba(52,211,153,0.35)", Icon: TrendingUp }
  : b === "Bearish" ? { c: "ws-down", bg: "rgba(248,113,113,0.12)", bd: "rgba(248,113,113,0.35)", Icon: TrendingDown }
  : { c: "text-[color:var(--ws-muted)]", bg: "rgba(148,163,184,0.10)", bd: "rgba(148,163,184,0.28)", Icon: Minus };

const impactTone = (i) =>
  i === "High" ? { dot: "#f87171", label: "text-[#fca5a5]" }
  : i === "Medium" ? { dot: "#fbbf24", label: "text-[#fcd34d]" }
  : { dot: "#64748b", label: "text-[color:var(--ws-muted)]" };

const timeAgo = (ts) => {
  if (!ts) return "";
  const s = Math.max(0, Date.now() / 1000 - ts);
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const shortCd = (ms) => {
  if (ms <= 0) return "LIVE";
  const s = Math.floor(ms / 1000), d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
};

const SourceTag = ({ s }) => (
  <span className="text-[9px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-md font-semibold"
    style={{ background: "rgba(59,130,246,0.14)", color: "var(--ws-blue-bright)" }}>{s}</span>
);

const CatChip = ({ cat }) => {
  const m = catMeta(cat);
  return (
    <span className="text-[9px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-md font-bold inline-flex items-center gap-1"
      style={{ background: m.bg, color: m.c }}>
      <m.Icon className="w-3 h-3" /> {cat}
    </span>
  );
};

const InstTag = ({ t }) => (
  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide"
    style={{ background: "rgba(148,163,184,0.12)", color: "#cbd5e1" }}>{t}</span>
);

const NewsThumb = ({ src, cat, className = "" }) => {
  const [ok, setOk] = useState(!!src);
  const m = catMeta(cat);
  if (src && ok) {
    return <img src={src} alt="" onError={() => setOk(false)} className={`object-cover ${className}`} loading="lazy" />;
  }
  return (
    <div className={`flex items-center justify-center ${className}`} style={{ background: `linear-gradient(135deg, ${m.bg}, rgba(15,23,42,0.6))` }}>
      <m.Icon className="w-7 h-7" style={{ color: m.c, opacity: 0.85 }} />
    </div>
  );
};

const PairCard = ({ p }) => {
  const t = biasTone(p.bias);
  return (
    <div className="ws-panel ws-panel-hover p-4" data-testid={`sent-card-${p.key}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="ws-heading font-bold text-[13px] text-white">{p.key}</div>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md inline-flex items-center gap-1"
          style={{ background: t.bg, border: `1px solid ${t.bd}` }}>
          <t.Icon className={`w-3 h-3 ${t.c}`} /> <span className={t.c}>{p.bias}</span>
        </span>
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="ws-heading text-[15px] font-bold text-white">
          {p.current?.toLocaleString(undefined, { maximumFractionDigits: 3 })}
        </span>
        <span className={`text-[11px] font-semibold ${p.change_pct > 0 ? "ws-up" : p.change_pct < 0 ? "ws-down" : "text-[color:var(--ws-muted)]"}`}>
          {p.change_pct > 0 ? "+" : ""}{p.change_pct}%
        </span>
      </div>
      {/* S -> R position bar */}
      <div className="relative h-1.5 rounded-full bg-[rgba(148,163,184,0.15)] mb-1">
        <div className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full"
          style={{ left: `calc(${Math.round((p.position ?? 0.5) * 100)}% - 5px)`, background: t.dot || "var(--ws-blue-bright)" }} />
      </div>
      <div className="flex justify-between text-[9px] text-[color:var(--ws-muted)]">
        <span>S {p.support ?? "—"}</span><span>R {p.resistance ?? "—"}</span>
      </div>
    </div>
  );
};

const Drawer = ({ open, title, onClose, children }) => (
  <div className={`ws-news-drawer ${open ? "open" : ""}`} data-testid="news-drawer">
    <div className="ws-news-drawer-overlay" onClick={onClose} />
    <div className="ws-news-drawer-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="ws-heading font-bold text-[16px] leading-snug pr-2">{title}</h3>
        <button onClick={onClose} data-testid="news-drawer-close" className="ws-btn-ghost w-8 h-8 rounded-lg flex items-center justify-center shrink-0"><X className="w-4 h-4" /></button>
      </div>
      {children}
    </div>
  </div>
);

const PairChips = ({ pairs }) => (
  <div className="flex flex-wrap gap-1.5">
    {(pairs || []).map((x, i) => (
      <span key={i} className="text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ background: "rgba(59,130,246,0.14)", color: "var(--ws-blue-bright)" }}>{x}</span>
    ))}
  </div>
);

const Block = ({ label, children }) => (
  <div className="mb-4">
    <div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)] mb-1.5">{label}</div>
    <div className="text-[13px] text-[color:var(--ws-text)] opacity-95 leading-relaxed">{children}</div>
  </div>
);

const WSNewsPro = () => {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [sentiment, setSentiment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impactFilter, setImpactFilter] = useState("all");
  const [now, setNow] = useState(Date.now());
  const [drawer, setDrawer] = useState({ open: false, kind: null, title: "", loading: false, data: null });

  const load = async () => {
    setLoading(true);
    try {
      const [n, c, s] = await Promise.all([
        fetch(`${API}/news/top?limit=24`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/news/calendar`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/news/sentiment`, { credentials: "include" }).then((r) => r.json()),
      ]);
      setNews(n.news || []);
      setEvents(c.events || []);
      setSentiment(s);
    } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const openNews = async (item) => {
    setDrawer({ open: true, kind: "news", title: item.title, loading: true, data: { link: item.link, source: item.source, category: item.category, tags: item.tags, image: item.image, ts: item.ts } });
    try {
      const d = await (await fetch(`${API}/news/summarize`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: item.title, summary: item.summary }),
      })).json();
      setDrawer((p) => ({ ...p, loading: false, data: { ...p.data, ...d } }));
    } catch { setDrawer((p) => ({ ...p, loading: false })); }
  };

  const openEvent = async (ev) => {
    setDrawer({ open: true, kind: "event", title: ev.title, loading: true, data: { ...ev } });
    try {
      const d = await (await fetch(`${API}/news/event-view`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: ev.title, currency: ev.currency, impact: ev.impact, forecast: ev.forecast, previous: ev.previous }),
      })).json();
      setDrawer((p) => ({ ...p, loading: false, data: { ...p.data, ...d } }));
    } catch { setDrawer((p) => ({ ...p, loading: false })); }
  };

  const closeDrawer = () => setDrawer((p) => ({ ...p, open: false }));

  const filteredEvents = useMemo(() => {
    let e = events;
    if (impactFilter === "high") e = e.filter((x) => x.impact === "High");
    else if (impactFilter === "medhigh") e = e.filter((x) => x.impact === "High" || x.impact === "Medium");
    else if (impactFilter === "key") e = e.filter((x) => x.key);
    return e.slice(0, 40);
  }, [events, impactFilter]);

  const ai = sentiment?.ai;

  return (
    <div className="max-w-[1280px] mx-auto px-8 py-8">
      <AutoNewsAdmin />
      <NewsAlertsAdmin />
      <MaintenanceAdmin />
      {/* Header */}
      <div className="flex items-end justify-between mb-2 ws-fade-up">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase font-medium mb-2">
            <Radio className="w-3.5 h-3.5 text-[color:var(--ws-cyan)]" /> Live market intelligence
          </div>
          <h1 className="ws-heading text-[42px] font-bold ws-title-gradient inline-flex items-center gap-3">
            <Newspaper className="w-8 h-8 text-[color:var(--ws-blue-bright)]" /> WS News Pro
          </h1>
        </div>
        <button onClick={load} disabled={loading} data-testid="news-refresh" className="ws-btn-ghost px-3 py-2 rounded-lg text-[12px] font-medium inline-flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>
      <p className="text-[color:var(--ws-muted)] text-sm mb-6 ws-fade-up" style={{ animationDelay: "40ms" }}>
        Real-time news from Investing.com, live sentiment on major pairs, and the Forex Factory calendar — with AI on every headline &amp; event.
      </p>

      {/* AI Market Read banner */}
      {ai && (
        <div className="ws-panel p-5 mb-5 ws-fade-up" data-testid="news-ai-read"
          style={{ animationDelay: "60ms", background: "linear-gradient(135deg, rgba(59,130,246,0.10), rgba(139,92,246,0.08))" }}>
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles className="w-4 h-4 text-[color:var(--ws-blue-bright)]" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)]">AI Market Read</span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-md" style={{ background: "rgba(139,92,246,0.16)", color: "#c4b5fd" }}>{ai.risk_mood}</span>
          </div>
          <h3 className="ws-heading text-[18px] font-semibold mb-1">{ai.headline}</h3>
          <p className="text-[13px] text-[color:var(--ws-text)] opacity-90 mb-1.5">{ai.read}</p>
          {ai.watch && <p className="text-[12px] text-[color:var(--ws-blue-bright)]">👁 {ai.watch}</p>}
        </div>
      )}

      {/* Next high-impact event — live countdown */}
      {events.length > 0 && (
        <div className="mb-6 ws-fade-up" style={{ animationDelay: "70ms" }}>
          <EventCountdown variant="banner" events={events} onOpen={openEvent} />
        </div>
      )}

      {/* Sentiment strip */}
      <div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)] mb-3 ws-fade-up" style={{ animationDelay: "80ms" }}>Market Sentiment · live price action</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-8 ws-fade-up" style={{ animationDelay: "100ms" }} data-testid="news-sentiment-strip">
        {(sentiment?.pairs || []).map((p) => <PairCard key={p.key} p={p} />)}
        {!sentiment && loading && <div className="col-span-full text-center text-[color:var(--ws-muted)] py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
      </div>

      {/* News + Calendar */}
      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6">
        {/* Top News */}
        <div data-testid="news-top-list">
          <div className="flex items-center gap-2 mb-3">
            <span className="ws-live-dot" />
            <h2 className="ws-heading text-[16px] font-semibold">Top News</h2>
            <span className="text-[11px] text-[color:var(--ws-muted)]">Investing.com · tap for AI read</span>
          </div>
          {loading && !news.length ? (
            <div className="ws-panel p-8 text-center text-[color:var(--ws-muted)]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : news.length === 0 ? (
            <div className="ws-panel p-8 text-center text-[color:var(--ws-muted)] text-sm">No news available right now.</div>
          ) : (
            <div className="space-y-2.5">
              {/* Featured hero */}
              {news[0] && (
                <button onClick={() => openNews(news[0])} data-testid="news-item-0"
                  className="ws-panel ws-panel-hover ws-news-featured w-full text-left overflow-hidden block group relative">
                  <div className="relative h-[200px]">
                    <NewsThumb src={news[0].image} cat={news[0].category} className="w-full h-full" />
                    <div className="ws-news-hero-overlay" />
                    <span className="absolute top-3 right-3 ws-news-ai-pill"><Sparkles className="w-3 h-3" /> AI read</span>
                    <div className="absolute left-0 right-0 bottom-0 p-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <CatChip cat={news[0].category} />
                        {(news[0].tags || []).map((t, i) => <InstTag key={i} t={t} />)}
                        <span className="text-[10px] text-[color:var(--ws-muted)] ml-auto">{timeAgo(news[0].ts)}</span>
                      </div>
                      <h3 className="ws-heading font-bold text-white text-[19px] leading-tight drop-shadow">{news[0].title}</h3>
                      <div className="text-[11px] text-[color:var(--ws-muted)] mt-1.5 flex items-center gap-1.5">{news[0].source} · Investing.com</div>
                    </div>
                  </div>
                </button>
              )}
              {/* Rest as rich rows */}
              {news.slice(1).map((n, i) => (
                <button key={i + 1} onClick={() => openNews(n)} data-testid={`news-item-${i + 1}`}
                  className="ws-panel ws-panel-hover w-full text-left flex gap-3.5 p-3 group items-stretch">
                  <NewsThumb src={n.image} cat={n.category} className="w-[104px] h-[76px] rounded-lg shrink-0" />
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <CatChip cat={n.category} />
                      {(n.tags || []).map((t, k) => <InstTag key={k} t={t} />)}
                      <span className="text-[10px] text-[color:var(--ws-muted)] ml-auto">{timeAgo(n.ts)}</span>
                    </div>
                    <div className="ws-heading font-semibold text-[14px] text-white leading-snug line-clamp-2 flex-1">{n.title}</div>
                    <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-[color:var(--ws-muted)]">
                      <span>{n.source}</span>
                      <span className="ws-news-ai-inline opacity-0 group-hover:opacity-100 transition-opacity ml-auto inline-flex items-center gap-1 text-[color:var(--ws-blue-bright)]">
                        <Sparkles className="w-3 h-3" /> AI read
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Events */}
        <div data-testid="news-calendar-list">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[color:var(--ws-blue-bright)]" />
              <h2 className="ws-heading text-[16px] font-semibold">Upcoming Events</h2>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mb-3">
            {[["all", "All"], ["medhigh", "Med+High"], ["high", "High"], ["key", "CPI/PPI/FOMC"]].map(([v, l]) => (
              <button key={v} onClick={() => setImpactFilter(v)} data-testid={`cal-filter-${v}`}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${impactFilter === v ? "ws-btn-primary" : "ws-btn-ghost"}`}>{l}</button>
            ))}
          </div>
          <div className="ws-panel p-2 max-h-[720px] overflow-y-auto">
            {loading && !events.length ? (
              <div className="p-8 text-center text-[color:var(--ws-muted)]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            ) : filteredEvents.length === 0 ? (
              <div className="p-8 text-center text-[color:var(--ws-muted)] text-sm">No events for this filter.</div>
            ) : filteredEvents.map((ev, i) => {
              const t = impactTone(ev.impact);
              return (
                <button key={i} onClick={() => openEvent(ev)} data-testid={`cal-item-${i}`}
                  className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors border-b border-[color:var(--ws-border)] last:border-0">
                  <div className="w-14 shrink-0 text-center">
                    <div className="text-[11px] font-bold text-white">{(ev.currency || "").slice(0, 3)}</div>
                    <div className="text-[9px] text-[color:var(--ws-muted)]">{ev.sort_ts ? istTime(ev.sort_ts) : ev.time}</div>
                  </div>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: t.dot }} title={ev.impact} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {ev.key && <Flame className="w-3 h-3 text-[#fbbf24] shrink-0" />}
                      <span className="text-[13px] font-medium text-white truncate">{ev.title}</span>
                    </div>
                    <div className="text-[10px] text-[color:var(--ws-muted)] mt-0.5">
                      {ev.sort_ts ? istDate(ev.sort_ts) : ev.date}{ev.forecast ? ` · F: ${ev.forecast}` : ""}{ev.previous ? ` · P: ${ev.previous}` : ""}
                    </div>
                  </div>
                  {ev.sort_ts && ev.sort_ts * 1000 > now && (ev.impact === "High" || ev.impact === "Medium" || ev.key) ? (
                    <span className="ws-evt-rowcd shrink-0" style={{ color: t.dot, borderColor: t.dot + "44", background: t.dot + "14" }}>
                      {shortCd(ev.sort_ts * 1000 - now)}
                    </span>
                  ) : (
                    <Sparkles className="w-3.5 h-3.5 text-[color:var(--ws-muted)] shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Drawer */}
      <Drawer open={drawer.open} title={drawer.title} onClose={closeDrawer}>
        {drawer.loading ? (
          <div className="py-10 text-center text-[color:var(--ws-muted)] text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> WS AI is reading this…
          </div>
        ) : drawer.kind === "news" ? (
          <div data-testid="news-drawer-body">
            {drawer.data?.image && (
              <div className="rounded-xl overflow-hidden mb-4 -mt-1"><NewsThumb src={drawer.data.image} cat={drawer.data.category} className="w-full h-[160px]" /></div>
            )}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {drawer.data?.category && <CatChip cat={drawer.data.category} />}
              {(drawer.data?.tags || []).map((t, i) => <InstTag key={i} t={t} />)}
              {drawer.data?.ts ? <span className="text-[10px] text-[color:var(--ws-muted)] ml-auto">{timeAgo(drawer.data.ts)}</span> : null}
            </div>
            <Block label="Summary">{drawer.data?.summary || "No summary available."}</Block>
            {drawer.data?.impact && <Block label="Market Impact">{drawer.data.impact}</Block>}
            {drawer.data?.bias && (
              <div className="mb-4">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)] mb-1.5">Bias</div>
                <span className="text-[13px] font-bold px-2.5 py-1 rounded-md inline-flex items-center gap-1"
                  style={biasTone(drawer.data.bias).bg ? { background: biasTone(drawer.data.bias).bg, border: `1px solid ${biasTone(drawer.data.bias).bd}` } : {}}>
                  <span className={biasTone(drawer.data.bias).c}>{drawer.data.bias}</span>
                </span>
              </div>
            )}
            {drawer.data?.pairs?.length > 0 && <div className="mb-4"><div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)] mb-1.5">Affected Pairs</div><PairChips pairs={drawer.data.pairs} /></div>}
            {drawer.data?.watch && <Block label="What to Watch">{drawer.data.watch}</Block>}
            {drawer.data?.link && (
              <a href={drawer.data.link} target="_blank" rel="noreferrer" className="ws-btn-ghost px-3 py-2 rounded-lg text-[12px] font-medium inline-flex items-center gap-2 mt-2" data-testid="news-drawer-source-link">
                Read full story <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <p className="text-[10px] text-[color:var(--ws-muted)] mt-5 opacity-80">AI-generated from the headline — educational, not financial advice.</p>
          </div>
        ) : (
          <div data-testid="event-drawer-body">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[12px] font-bold px-2 py-0.5 rounded-md" style={{ background: "rgba(59,130,246,0.14)", color: "var(--ws-blue-bright)" }}>{drawer.data?.currency}</span>
              <span className="text-[12px] px-2 py-0.5 rounded-md" style={{ background: impactTone(drawer.data?.impact).dot + "22", color: impactTone(drawer.data?.impact).dot }}>{drawer.data?.impact} impact</span>
              <span className="text-[11px] text-[color:var(--ws-muted)]">{drawer.data?.sort_ts ? `${istDateTime(drawer.data.sort_ts)} ${IST_LABEL}` : `${drawer.data?.date || ""} ${drawer.data?.time || ""}`}</span>
            </div>
            {(drawer.data?.forecast || drawer.data?.previous) && (
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="ws-price-cell p-3"><div className="text-[10px] uppercase text-[color:var(--ws-muted)]">Forecast</div><div className="ws-heading font-bold text-white">{drawer.data?.forecast || "—"}</div></div>
                <div className="ws-price-cell p-3"><div className="text-[10px] uppercase text-[color:var(--ws-muted)]">Previous</div><div className="ws-heading font-bold text-white">{drawer.data?.previous || "—"}</div></div>
              </div>
            )}
            {drawer.data?.what && <Block label="What is it">{drawer.data.what}</Block>}
            {drawer.data?.why && <Block label="Why it matters">{drawer.data.why}</Block>}
            {drawer.data?.pairs?.length > 0 && <div className="mb-4"><div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)] mb-1.5">Pairs to Watch</div><PairChips pairs={drawer.data.pairs} /></div>}
            {drawer.data?.higher && (
              <div className="rounded-xl p-3 mb-2 flex items-start gap-2.5" style={{ background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.2)" }}>
                <ArrowUpRight className="w-4 h-4 ws-up shrink-0 mt-0.5" /><div className="text-[13px] opacity-95"><b className="ws-up">Hotter than forecast:</b> {drawer.data.higher}</div>
              </div>
            )}
            {drawer.data?.lower && (
              <div className="rounded-xl p-3 mb-4 flex items-start gap-2.5" style={{ background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)" }}>
                <ArrowDownRight className="w-4 h-4 ws-down shrink-0 mt-0.5" /><div className="text-[13px] opacity-95"><b className="ws-down">Cooler than forecast:</b> {drawer.data.lower}</div>
              </div>
            )}
            {drawer.data?.risk && (
              <div className="rounded-xl p-3 flex items-start gap-2.5" style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)" }}>
                <AlertTriangle className="w-4 h-4 text-[#fbbf24] shrink-0 mt-0.5" /><div className="text-[13px] opacity-95">{drawer.data.risk}</div>
              </div>
            )}
            <p className="text-[10px] text-[color:var(--ws-muted)] mt-5 opacity-80">AI-generated macro view — educational, not financial advice.</p>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default WSNewsPro;
